// src/components/Cards/CardConfiguration/CardConfigEvent/CardConfigEvent.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import API_BASE_URL from "../../../../config/config.js";
import { useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  CHAMPIONSHIP_STATE,
  getChampionshipStateMeta,
} from "../../../../utils/championshipState.js";
import ImageUrlUploadField from "../../../Inputs/ImageUrlUploadField.jsx";
import DescargarProgramacionPdfModal from "./DescargarProgramacionPdfModal.jsx";
import DescargarResultadosPdfModal from "./DescargarResultadosPdfModal.jsx";

const PRIMARY_COLOR = "#2564eb";

const CHAMPIONSHIP_STATE_OPTIONS = [
  {
    value: "1",
    label: CHAMPIONSHIP_STATE[1].label,
    description: "El evento está visible, pero todavía no ha iniciado.",
  },
  {
    value: "2",
    label: CHAMPIONSHIP_STATE[2].label,
    description: "El evento está activo y aparecerá primero en los listados.",
  },
  {
    value: "3",
    label: CHAMPIONSHIP_STATE[3].label,
    description: "El evento está visible, pero se mostrará al final.",
  },
];

// FTR-0066: `onSaved` avisa a ListCategoriasSection tras guardar (p. ej. para mostrar u ocultar la pestaña Cómputo
// sin recargar la página) con lo que confirmó el backend: { computoGeneral: { enabled, applied } | null }.
const CardConfigEvent = ({ onSaved } = {}) => {
  const { idevent } = useParams();
  const { user } = useSelector((state) => state.auth);
  // FIX-EMERGENCIA: "Descargar programación PDF" es visible únicamente para rol 1. El backend
  // también valida el rol en /programacion-bulk-pdf: esta condición es solo UX, no el control real.
  const isGlobalAdmin = Number(user?.rol) === 1;

  const [scale, setScale] = useState("1");
  const [championshipState, setChampionshipState] = useState("1");
  const [flagAllDatesProg, setFlagAllDatesProg] = useState(false);
  // FTR-0046: versionamiento de uniformes de los deportes colectivos del evento.
  const [flagUniformVersioning, setFlagUniformVersioning] = useState(false);
  const [uniformVersioningAvailable, setUniformVersioningAvailable] = useState(true);
  // FTR-0066: Cómputo General del evento. Solo rol 1 lo ve y lo envía (el backend responde 403 a otro rol).
  const [flagComputoGeneral, setFlagComputoGeneral] = useState(false);
  const [computoGeneralAvailable, setComputoGeneralAvailable] = useState(true);
  const [headerPath, setHeaderPath] = useState("");
  const [footerPath, setFooterPath] = useState("");
  const [downloadPdfModalOpen, setDownloadPdfModalOpen] = useState(false);
  // FTR-0067: descarga de resultados PDF por deportes y fases (mismo alcance que la de programación: solo rol 1).
  const [downloadResultsModalOpen, setDownloadResultsModalOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchChampionshipData = async () => {
      if (!idevent) return;

      setLoading(true);

      try {
        const response = await axios.get(`${API_BASE_URL}/championship`, {
          params: { idevent },
        });

        if (cancelled) return;

        const {
          tipoAlcance,
          flag_all_dates_prog,
          header_path,
          footer_path,
          state,
          flag_uniform_versioning,
          uniformVersioningAvailable: versioningAvailable,
          flag_computo_general,
          computoGeneralAvailable: computoAvailable,
        } = response.data || {};

        setScale((tipoAlcance ?? 1).toString());
        const normalizedState = Number(state);

        setChampionshipState(
          [1, 2, 3].includes(normalizedState) ? normalizedState.toString() : "1"
        );
        setFlagAllDatesProg(
          Number(flag_all_dates_prog) === 1 ||
          flag_all_dates_prog === true ||
          flag_all_dates_prog === "true"
        );
        setHeaderPath(header_path || "");
        setFooterPath(footer_path || "");
        setFlagUniformVersioning(Number(flag_uniform_versioning) === 1);
        setUniformVersioningAvailable(versioningAvailable !== false);
        setFlagComputoGeneral(Number(flag_computo_general) === 1);
        setComputoGeneralAvailable(computoAvailable !== false);
      } catch (error) {
        if (!cancelled) {
          toast.error("¡Hubo un Problema al Cargar la Información del Evento!");
          console.error("Error al cargar el evento", error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchChampionshipData();

    return () => {
      cancelled = true;
    };
  }, [idevent]);

  const handleScaleChange = (event) => {
    setScale(event.target.value);
  };

  const handleChampionshipStateChange = (event) => {
    setChampionshipState(event.target.value);
  };

  const handleSave = async () => {
    if (!idevent) {
      toast.error("No se encontró el ID del evento.");
      return;
    }

    setSaving(true);

    try {
      const response = await axios.patch(`${API_BASE_URL}/championship`, {
        idchampionship: Number(idevent),
        tipoAlcance: Number(scale),
        state: Number(championshipState),
        flag_all_dates_prog: flagAllDatesProg ? 1 : 0,
        header_path: headerPath.trim() || null,
        footer_path: footerPath.trim() || null,
        // FTR-0046: solo si el backend tiene la columna (si no, el interruptor está deshabilitado).
        ...(uniformVersioningAvailable ? { flag_uniform_versioning: flagUniformVersioning ? 1 : 0 } : {}),
        // FTR-0066: solo rol 1 y solo si el backend tiene la columna (si no, el interruptor está deshabilitado).
        ...(isGlobalAdmin && computoGeneralAvailable ? { flag_computo_general: flagComputoGeneral ? 1 : 0 } : {}),
      });

      toast.success("¡Se actualizó el evento!");
      const versioning = response?.data?.uniformVersioning;
      if (versioning?.applied === false) {
        toast.error("El versionamiento de uniformes todavía no está disponible (falta la actualización de base de datos).");
      } else if (versioning?.initialized?.created) {
        toast.success(`Versionamiento activado: V1 creada en ${versioning.initialized.created} deporte(s) colectivo(s).`);
      }
      const computoGeneral = response?.data?.computoGeneral || null;
      if (computoGeneral?.applied === false) {
        toast.error("El Cómputo General todavía no está disponible (falta la actualización de base de datos).");
      }
      onSaved?.({ computoGeneral });
    } catch (error) {
      // FTR-0066: los guards con código (p. ej. 403 ROLE_NOT_ALLOWED) explican el motivo; el resto, mensaje genérico.
      const guardMessage = error?.response?.data?.code ? error.response.data.msg : null;
      toast.error(guardMessage || "¡Hubo un Problema al Actualizar el Evento!");
      console.error("Error al actualizar el evento", error);
    } finally {
      setSaving(false);
    }
  };

  const scaleOptions = [
    {
      value: "1",
      label: "Local",
      description: "Evento de alcance local.",
    },
    {
      value: "2",
      label: "Regional",
      description: "Evento de alcance regional.",
    },
    {
      value: "3",
      label: "Internacional",
      description: "Evento de alcance internacional.",
    },
  ];

  const selectedStateMeta = getChampionshipStateMeta(championshipState);

  return (
    <div className="mx-auto w-full max-w-4xl rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div
        className="rounded-t-2xl px-6 py-5 text-white"
        style={{ backgroundColor: PRIMARY_COLOR }}
      >
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-100">
          Ajustes generales
        </p>
        <h2 className="mt-1 text-2xl font-black">Configuración del Evento</h2>
        <p className="mt-2 text-sm font-medium text-blue-50">
          Administra el estado, alcance del evento, visualización de
          programación y recursos gráficos de cabecera/pie.
        </p>
      </div>

      <div className="space-y-8 p-6">
        {loading ? (
          <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-bold text-blue-700">
            Cargando configuración del evento...
          </div>
        ) : null}

        <section className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h3 className="text-lg font-black text-slate-900">
                Estado del Evento
              </h3>
              <p className="mt-1 text-sm font-medium text-slate-500">
                Define cómo se mostrará el evento en los listados de WINSCORE y
                HAYLLIS.
              </p>
            </div>

            <span
              className={`inline-flex w-fit shrink-0 rounded-full px-3 py-1 text-xs font-black ring-1 ${selectedStateMeta.className}`}
            >
              Estado actual: {selectedStateMeta.label}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {CHAMPIONSHIP_STATE_OPTIONS.map((option) => {
              const selected = championshipState === option.value;
              const stateMeta = getChampionshipStateMeta(option.value);

              return (
                <label
                  key={option.value}
                  className={[
                    "cursor-pointer rounded-2xl border bg-white p-4 shadow-sm transition",
                    selected
                      ? "border-blue-600 ring-2 ring-blue-100"
                      : "border-slate-200 hover:border-blue-300",
                    loading || saving ? "cursor-not-allowed opacity-70" : "",
                  ].join(" ")}
                >
                  <div className="flex h-full flex-col gap-3">
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        className="mt-1 h-5 w-5 cursor-pointer border-slate-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                        name="championshipState"
                        value={option.value}
                        checked={selected}
                        onChange={handleChampionshipStateChange}
                        disabled={loading || saving}
                      />

                      <div className="min-w-0">
                        <span
                          className={[
                            "block text-base font-black",
                            selected ? "text-blue-700" : "text-slate-800",
                          ].join(" ")}
                        >
                          {option.label}
                        </span>

                        <span
                          className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-[11px] font-black ring-1 ${stateMeta.className}`}
                        >
                          state = {option.value}
                        </span>
                      </div>
                    </div>

                    <p className="text-xs font-semibold leading-5 text-slate-500">
                      {option.description}
                    </p>
                  </div>
                </label>
              );
            })}
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
          <div className="mb-5">
            <h3 className="text-lg font-black text-slate-900">
              Escala del Evento
            </h3>
            <p className="mt-1 text-sm font-medium text-slate-500">
              Define el alcance competitivo o institucional del evento.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {scaleOptions.map((option) => {
              const selected = scale === option.value;

              return (
                <label
                  key={option.value}
                  className={[
                    "cursor-pointer rounded-2xl border bg-white p-4 shadow-sm transition",
                    selected
                      ? "border-blue-600 ring-2 ring-blue-100"
                      : "border-slate-200 hover:border-blue-300",
                    loading || saving ? "cursor-not-allowed opacity-70" : "",
                  ].join(" ")}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      className="mt-1 h-5 w-5 cursor-pointer border-slate-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                      name="eventScale"
                      value={option.value}
                      checked={selected}
                      onChange={handleScaleChange}
                      disabled={loading || saving}
                    />

                    <div>
                      <span
                        className={[
                          "block text-base font-black",
                          selected ? "text-blue-700" : "text-slate-800",
                        ].join(" ")}
                      >
                        {option.label}
                      </span>
                      <span className="mt-1 block text-xs font-semibold leading-5 text-slate-500">
                        {option.description}
                      </span>
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-black text-slate-900">
                Programación
              </h3>
              <p className="mt-1 text-sm font-medium text-slate-500">
                Controla si la programación debe considerar todas las fechas del
                evento.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setFlagAllDatesProg((prev) => !prev)}
              disabled={loading || saving}
              className={[
                "relative inline-flex h-8 w-16 shrink-0 items-center rounded-full transition",
                flagAllDatesProg ? "bg-blue-600" : "bg-slate-300",
                loading || saving ? "cursor-not-allowed opacity-60" : "",
              ].join(" ")}
              aria-pressed={flagAllDatesProg}
              title="Activar o desactivar todas las fechas en programación"
            >
              <span
                className={[
                  "inline-block h-7 w-7 transform rounded-full bg-white shadow transition",
                  flagAllDatesProg ? "translate-x-8" : "translate-x-1",
                ].join(" ")}
              />
            </button>
          </div>

          <div
            className={[
              "mt-4 rounded-xl border px-4 py-3 text-sm font-bold",
              flagAllDatesProg
                ? "border-blue-200 bg-blue-50 text-blue-700"
                : "border-slate-200 bg-slate-50 text-slate-600",
            ].join(" ")}
          >
            {flagAllDatesProg
              ? "Activo: Se considerarán todas las fechas en programación."
              : "Inactivo: La programación sale fecha por fecha."}
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-black text-slate-900">
                Versionamiento de uniformes
              </h3>
              <p className="mt-1 text-sm font-medium text-slate-500">
                En los deportes colectivos, trabaja los uniformes por versiones
                (V1, V2…) y alterna entre ellas desde Programación sin perder
                nada.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setFlagUniformVersioning((prev) => !prev)}
              disabled={loading || saving || !uniformVersioningAvailable}
              className={[
                "relative inline-flex h-8 w-16 shrink-0 items-center rounded-full transition",
                flagUniformVersioning ? "bg-blue-600" : "bg-slate-300",
                loading || saving || !uniformVersioningAvailable ? "cursor-not-allowed opacity-60" : "",
              ].join(" ")}
              aria-pressed={flagUniformVersioning}
              aria-label="Activar versionamiento de uniformes"
              title="Activar o desactivar el versionamiento de uniformes"
            >
              <span
                className={[
                  "inline-block h-7 w-7 transform rounded-full bg-white shadow transition",
                  flagUniformVersioning ? "translate-x-8" : "translate-x-1",
                ].join(" ")}
              />
            </button>
          </div>

          <div
            className={[
              "mt-4 rounded-xl border px-4 py-3 text-sm font-bold",
              flagUniformVersioning
                ? "border-blue-200 bg-blue-50 text-blue-700"
                : "border-slate-200 bg-slate-50 text-slate-600",
            ].join(" ")}
          >
            {!uniformVersioningAvailable
              ? "No disponible: falta la actualización de base de datos del versionamiento."
              : flagUniformVersioning
                ? "Activo: cada deporte colectivo tiene su V1 con los uniformes actuales; en Programación puedes crear otras versiones y alternar entre ellas."
                : "Inactivo: los uniformes se trabajan sin versiones. Si había versiones, se conservan ocultas."}
          </div>
        </section>

        {isGlobalAdmin ? (
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-lg font-black text-slate-900">
                  Habilitar Cómputo General
                </h3>
                <p className="mt-1 text-sm font-medium text-slate-500">
                  Muestra la pestaña Cómputo (solo administradores) y en Hayllis
                  2.0 cambia «Medallero» por «Cómputo General» en este evento.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setFlagComputoGeneral((prev) => !prev)}
                disabled={loading || saving || !computoGeneralAvailable}
                className={[
                  "relative inline-flex h-8 w-16 shrink-0 items-center rounded-full transition",
                  flagComputoGeneral ? "bg-blue-600" : "bg-slate-300",
                  loading || saving || !computoGeneralAvailable ? "cursor-not-allowed opacity-60" : "",
                ].join(" ")}
                aria-pressed={flagComputoGeneral}
                aria-label="Habilitar Cómputo General"
                title="Activar o desactivar el Cómputo General del evento"
              >
                <span
                  className={[
                    "inline-block h-7 w-7 transform rounded-full bg-white shadow transition",
                    flagComputoGeneral ? "translate-x-8" : "translate-x-1",
                  ].join(" ")}
                />
              </button>
            </div>

            <div
              className={[
                "mt-4 rounded-xl border px-4 py-3 text-sm font-bold",
                flagComputoGeneral
                  ? "border-blue-200 bg-blue-50 text-blue-700"
                  : "border-slate-200 bg-slate-50 text-slate-600",
              ].join(" ")}
            >
              {!computoGeneralAvailable
                ? "No disponible: falta la actualización de base de datos del Cómputo General."
                : flagComputoGeneral
                  ? "Activo: la pestaña Cómputo aparece al guardar y Hayllis 2.0 muestra «Cómputo General» en lugar de «Medallero» en este evento."
                  : "Inactivo: el evento mantiene su Medallero en Hayllis 2.0. Lo cargado en el cómputo se conserva."}
            </div>
          </section>
        ) : null}

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-5">
            <h3 className="text-lg font-black text-slate-900">
              Recursos gráficos
            </h3>
            <p className="mt-1 text-sm font-medium text-slate-500">
              Define las rutas de cabecera y pie de cabecera del membrete del
              evento.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <ImageUrlUploadField
              label="Header path"
              value={headerPath}
              onChange={setHeaderPath}
              idevent={idevent}
              assetType="header"
              disabled={loading || saving}
              placeholder="https://.../header.png"
            />

            <ImageUrlUploadField
              label="Footer path"
              value={footerPath}
              onChange={setFooterPath}
              idevent={idevent}
              assetType="footer"
              disabled={loading || saving}
              placeholder="https://.../footer.png"
            />
          </div>
        </section>

        {isGlobalAdmin ? (
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-lg font-black text-slate-900">
                  Descargar programación PDF
                </h3>
                <p className="mt-1 text-sm font-medium text-slate-500">
                  Genera la programación completa (todas las fechas) de varios deportes
                  colectivos, por fase de grupos, fase final o ambas.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setDownloadPdfModalOpen(true)}
                className="inline-flex shrink-0 items-center justify-center rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-black text-blue-700 shadow-sm transition hover:bg-blue-100"
              >
                Descargar programación PDF
              </button>
            </div>
          </section>
        ) : null}

        {isGlobalAdmin ? (
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-lg font-black text-slate-900">
                  Descargar resultados PDF
                </h3>
                <p className="mt-1 text-sm font-medium text-slate-500">
                  Genera los resultados de varios deportes colectivos por fase de grupos, fase
                  final o ambas, con tabla de posiciones, acumulada o ubicación final si quieres.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setDownloadResultsModalOpen(true)}
                className="inline-flex shrink-0 items-center justify-center rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-black text-blue-700 shadow-sm transition hover:bg-blue-100"
              >
                Descargar resultados PDF
              </button>
            </div>
          </section>
        ) : null}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={loading || saving}
            className="inline-flex min-w-[180px] items-center justify-center rounded-xl px-6 py-3 text-sm font-black text-white shadow-sm transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
            style={{ backgroundColor: PRIMARY_COLOR }}
          >
            {saving ? "Guardando..." : "Guardar configuración"}
          </button>
        </div>
      </div>

      {isGlobalAdmin ? (
        <DescargarProgramacionPdfModal
          open={downloadPdfModalOpen}
          onClose={() => setDownloadPdfModalOpen(false)}
          idevent={idevent}
        />
      ) : null}

      {isGlobalAdmin ? (
        <DescargarResultadosPdfModal
          open={downloadResultsModalOpen}
          onClose={() => setDownloadResultsModalOpen(false)}
          idevent={idevent}
        />
      ) : null}
    </div>
  );
};

export default CardConfigEvent;
